import {
  Directive,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  Inject,
  ViewContainerRef,
  SimpleChanges
} from '@angular/core';
import {
  FormGroupState,
  FormControlState
} from 'ngrx-forms';
import {
  MemoizedSelector,
  Store
} from '@ngrx/store';
import { Subscription } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { deepEqual } from '@digitaix/helpers';
import {
  filter,
  first,
  map,
  tap,
  debounceTime,
  distinctUntilChanged
} from 'rxjs/operators';
import { BASE_GROUP_STATE_SELECTOR } from './tokens';

@Directive({
  selector: '[dxGroupControl]'
})
export class DxFormGroupDirective<
  TStateValue,
  TViewValue = TStateValue
  > implements OnInit, OnDestroy, OnChanges {

  @Input() public dxGroupControl: string | null = null;

  public readonly baseSelector: MemoizedSelector<any, FormGroupState<any>>;

  private _subscriptions = new Subscription();

  constructor(
    public readonly store$: Store<any>,
    public readonly logger: NGXLogger,
    @Inject(BASE_GROUP_STATE_SELECTOR) baseSelector: any,
    public readonly view: ViewContainerRef
  ) {
    this.baseSelector = baseSelector;
  }

  public ngOnInit(): void {
    if (!this.dxGroupControl) {
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxGroupControl
          }] the dxFormControl input is not defined`
      );
    }
    this.logger.trace(
      `[DxFormControlDirective::ngOnInit::${this.dxGroupControl}] call`
    );

    if (!this.baseSelector) {
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxGroupControl
          }] the BASE_GROUP_STATE_SELECTOR Injector is not defined`
      );
    }

    this._subscriptions.add(
      this.store$.select(this.baseSelector)
          .pipe(
            filter(Boolean),
            first(),
            map(baseState => this.applyControlPath(baseState, this.dxGroupControl)),
            tap(state => this.updateNgrxFormControlState(state)),
          ).subscribe()
    );

    this._subscriptions.add(
      this.store$
          .select(this.baseSelector)
          .pipe(
            filter(Boolean),
            debounceTime(250),
            distinctUntilChanged(deepEqual),
            map(baseState => this.applyControlPath(baseState, this.dxGroupControl)),
            distinctUntilChanged(deepEqual),
            tap(state => this.updateNgrxFormControlState(state)),
          )
          .subscribe()
    );
  }

  private updateNgrxFormControlState(state: FormControlState<any>): void {
    this.logger.trace(
      `[DxFormControlDirective::updateNgrxFormControlState::${this.dxGroupControl}] call`,
      state
    );
    (<any>this.view)._data.componentView.component.ngrxFormGroup = state;
  }

  private applyControlPath(
    baseState: FormGroupState<any>,
    controlPath: string
  ): FormControlState<any> {
    const targetState = controlPath.split('/').reduce((state, controlId) => {
      if (state.controls.hasOwnProperty(controlId)) {
        return state.controls[controlId];
      }
      this.logger.error(
        `[DxFormControlDirective::ngOnInit::${controlPath}] control path is not valid`,
        { dxFormControl: controlPath, baseState }
      );
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${controlPath}] control path is not valid`
      );
    }, baseState);

    return targetState as FormControlState<any>;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['dxGroupControl'] &&
      changes['dxGroupControl'].previousValue &&
      changes['dxGroupControl'].previousValue !==
      changes['dxGroupControl'].currentValue
    ) {
      this.logger.error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxGroupControl
          }] the dxFormControl input should not change`,
        changes['dxFormControl']
      );
      throw new Error(
        `[DxFormControlDirective::ngOnInit::${
          this.dxGroupControl
          }] the dxFormControl input should not change`
      );
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._subscriptions = new Subscription();
  }
}
